import { useCallback, useEffect, useRef, useState } from 'react';
import { SendAdminProspectWhatsAppAPI } from '../../../../api/AdminAPI';
import { BULK_INTERVAL_SECONDS, defaultMessage } from '../prospectOptions';

const IDLE = { running: false, finished: false, sent: 0, failed: [], current: null, waitLeft: 0 };

/** @returns {number} segundos sorteados dentro de BULK_INTERVAL_SECONDS */
function randomInterval() {
  const [min, max] = BULK_INTERVAL_SECONDS;
  return min + Math.floor(Math.random() * (max - min + 1));
}

/**
 * @param {Object} payload
 * @returns {Promise<Object>} resposta de admin/prospects/send_whatsapp.php
 */
function sendOne(payload) {
  return new Promise((resolve) => SendAdminProspectWhatsAppAPI(payload, resolve));
}

/**
 * useProspectBulkSend
 *
 * Envia o lote pelo número da Gabriela, um contato por vez, com intervalo sorteado
 * entre um e outro. Roda no navegador: a aba precisa ficar aberta, e fechar o diálogo
 * para o lote (nada vai para a fila automática). O texto de cada contato é montado na
 * hora do envio, para o "Bom dia/Boa tarde" bater com a hora.
 *
 * @param {Function} onSent recebe o contato atualizado a cada envio que deu certo
 * @returns {{ state: Object, start: Function, stop: Function, reset: Function }}
 */
function useProspectBulkSend(onSent) {
  const [state, setState] = useState(IDLE);
  const stopRef = useRef(false);

  useEffect(() => () => { stopRef.current = true; }, []);

  /** Espera os segundos mostrando a contagem; devolve false se o lote foi parado. */
  const wait = useCallback(async (seconds) => {
    for (let left = seconds; left > 0; left -= 1) {
      if (stopRef.current) return false;
      setState((prev) => ({ ...prev, waitLeft: left }));
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    setState((prev) => ({ ...prev, waitLeft: 0 }));
    return !stopRef.current;
  }, []);

  /** @param {Array<{ prospect: Object, variant: string }>} queue */
  const start = useCallback(async (queue) => {
    stopRef.current = false;
    setState({ ...IDLE, running: true });

    for (let index = 0; index < queue.length; index += 1) {
      // eslint-disable-next-line no-await-in-loop
      if (index > 0 && !(await wait(randomInterval()))) break;
      const { prospect, variant } = queue[index];
      setState((prev) => ({ ...prev, current: prospect.name }));
      // eslint-disable-next-line no-await-in-loop
      const response = await sendOne({ prospect_id: prospect.id, message: defaultMessage(prospect, variant), variant });
      if (response?.success) {
        onSent(response.item);
        setState((prev) => ({ ...prev, sent: prev.sent + 1 }));
      } else {
        const error = response?.error || 'Não foi possível enviar.';
        setState((prev) => ({ ...prev, failed: [...prev.failed, { name: prospect.name, error }] }));
      }
    }

    setState((prev) => ({ ...prev, running: false, finished: true, current: null, waitLeft: 0 }));
  }, [onSent, wait]);

  const stop = useCallback(() => { stopRef.current = true; }, []);
  const reset = useCallback(() => setState(IDLE), []);

  return { state, start, stop, reset };
}

export default useProspectBulkSend;
